import React, { useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { initiateTransfer } from "../../../../lib/api/claim.api";
import type { ClaimerRole } from "../../../../lib/types/claim.types";
import {
  ArrowLeftRight,
  FileText,
  Upload,
  X,
  AlertCircle,
  ChevronRight,
  Shield,
} from "lucide-react";
import { useAuth } from "../../../../Context/AuthContext";

const ROLES: { value: ClaimerRole; label: string }[] = [
  { value: "Owner", label: "Owner" },
  { value: "Manager", label: "Manager" },
  { value: "Authorized staff", label: "Authorized Staff" },
  { value: "Marketing agency", label: "Marketing Agency" },
];

const ALLOWED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
];
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

const TERMS = [
  "I confirm I have the legal right to claim ownership of this listing.",
  "I understand that providing false information may result in account suspension.",
  "I agree to comply with platform terms of service and community guidelines.",
];

export default function TransferRequestPage() {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [role, setRole] = useState<ClaimerRole | "">("");
  const [reason, setReason] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const canSubmit = role && reason.trim().length >= 20 && file && !loading && termsAccepted;

  const validateFile = (f: File): string | null => {
    if (!ALLOWED_TYPES.includes(f.type))
      return "File type not supported. Please upload JPG, PNG, WEBP, HEIC, HEIF, or PDF.";
    if (f.size > MAX_SIZE_BYTES)
      return "File too large. Maximum 10 MB.";
    return null;
  };

  const handleFileSelect = (f: File) => {
    const err = validateFile(f);
    if (err) { setFileError(err); setFile(null); return; }
    setFileError(null);
    setFile(f);
    setApiError(null);
  };

  const formatSize = (bytes: number) =>
    bytes < 1024 * 1024
      ? `${(bytes / 1024).toFixed(1)} KB`
      : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

  const handleSubmit = async () => {
    if (!canSubmit || !restaurantId || !file) return;
    setLoading(true);
    setApiError(null);

    try {
      const result = await initiateTransfer(
        restaurantId,
        role as ClaimerRole,
        reason,
        file,
        accessToken
      );
      navigate(`/transfer/${result.transferId}`);
    } catch (err: any) {
      setApiError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F7] font-['DM_Sans',sans-serif]">
      <div className="border-b border-[#E8E4DC] bg-white px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <ArrowLeftRight className="w-5 h-5 text-[#C17B3F]" />
          <span className="text-sm font-medium text-[#6B6460] tracking-wide uppercase">
            Request Ownership Transfer
          </span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="mb-10">
          <h1
            className="text-[2.25rem] font-semibold text-[#1C1917] leading-tight mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Request ownership
          </h1>
          <p className="text-[#6B6460] text-base leading-relaxed">
            The current owner will have 7 days to respond to your request.
            If they don't reply, the request will be escalated for admin review.
          </p>
        </div>

        {/* Role */}
        <div className="mb-6">
          <label className="block text-xs font-semibold uppercase tracking-widest text-[#6B6460] mb-3">
            Your role
          </label>
          <div className="grid grid-cols-2 gap-2">
            {ROLES.map((r) => (
              <button
                key={r.value}
                onClick={() => setRole(r.value)}
                className={`px-4 py-3 rounded-xl border-2 text-sm font-medium text-left transition-all duration-150 ${
                  role === r.value
                    ? "border-[#C17B3F] bg-[#FDF5EC] text-[#C17B3F]"
                    : "border-[#E8E4DC] bg-white text-[#44403C] hover:border-[#C17B3F]/40"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {/* Reason */}
        <div className="mb-6">
          <label className="block text-xs font-semibold uppercase tracking-widest text-[#6B6460] mb-3">
            Reason for transfer
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain why you should be the owner of this listing…"
            rows={4}
            className="w-full px-4 py-3.5 bg-white border-2 border-[#E8E4DC] rounded-xl text-sm text-[#1C1917] placeholder-[#C4B9AD] resize-none outline-none focus:border-[#C17B3F] focus:ring-2 focus:ring-[#C17B3F]/15 transition-all"
          />
          <p
            className={`text-xs mt-1.5 text-right ${
              reason.trim().length < 20 && reason.length > 0
                ? "text-amber-600"
                : "text-[#9B918B]"
            }`}
          >
            {reason.trim().length < 20
              ? `${20 - reason.trim().length} more characters required`
              : `${reason.length} characters`}
          </p>
        </div>

        {/* Proof upload */}
        <div className="mb-8">
          <label className="block text-xs font-semibold uppercase tracking-widest text-[#6B6460] mb-3">
            Supporting document <span className="text-red-500">*</span>
          </label>
          {!file ? (
            <div
              onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleFileSelect(f); }}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onClick={() => inputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200 ${
                dragOver
                  ? "border-[#C17B3F] bg-[#FDF5EC]"
                  : "border-[#D1C9BE] bg-white hover:border-[#C17B3F]/50"
              }`}
            >
              <Upload className="w-7 h-7 text-[#C17B3F] mx-auto mb-2" />
              <p className="text-sm font-medium text-[#1C1917] mb-1">
                Drop file or <span className="text-[#C17B3F]">browse</span>
              </p>
              <p className="text-xs text-[#9B918B]">PDF, JPG, PNG, WEBP, HEIC · Max 10 MB</p>
              <input
                ref={inputRef}
                type="file"
                accept={ALLOWED_TYPES.join(",")}
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); }}
              />
            </div>
          ) : (
            <div className="bg-white border-2 border-[#C17B3F]/30 rounded-xl p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#FDF5EC] flex items-center justify-center flex-shrink-0">
                <FileText className="w-4 h-4 text-[#C17B3F]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#1C1917] truncate">{file.name}</p>
                <p className="text-xs text-[#9B918B]">{formatSize(file.size)}</p>
              </div>
              <button
                onClick={() => { setFile(null); if (inputRef.current) inputRef.current.value = ""; }}
                className="p-1.5 rounded-lg hover:bg-[#F5F0EB] text-[#9B918B] hover:text-[#1C1917]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {fileError && (
            <div className="mt-3 flex items-start gap-2 text-sm text-red-600">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {fileError}
            </div>
          )}
        </div>

        <div className="mb-8 bg-white border border-[#E8E4DC] rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-4 h-4 text-[#C17B3F]" />
            <span className="text-xs font-semibold uppercase tracking-widest text-[#6B6460]">
              Terms of Verification
            </span>
          </div>
          <ul className="space-y-3 mb-5">
            {TERMS.map((term, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-1 w-4 h-4 rounded-full bg-[#F0EAE1] flex items-center justify-center flex-shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C17B3F]" />
                </span>
                <span className="text-sm text-[#44403C] leading-relaxed">
                  {term}
                </span>
              </li>
            ))}
          </ul>

          <label className="flex items-center gap-3 cursor-pointer group">
            <div
              onClick={() => setTermsAccepted(!termsAccepted)}
              className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all duration-150 ${
                termsAccepted
                  ? "bg-[#C17B3F] border-[#C17B3F]"
                  : "border-[#C4B9AD] group-hover:border-[#C17B3F]"
              }`}
            >
              {termsAccepted && (
                <svg
                  className="w-3 h-3 text-white"
                  viewBox="0 0 12 10"
                  fill="none"
                >
                  <path
                    d="M1 5L4.5 8.5L11 1.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
            <span className="text-sm font-medium text-[#1C1917]">
              I have read and agree to the above terms
            </span>
          </label>
        </div>

        {/* API error */}
        {apiError && (
          <div className="mb-6 flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-4">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{apiError}</p>
          </div>
        )}

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={`w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-semibold text-sm tracking-wide transition-all duration-200 ${
            canSubmit
              ? "bg-[#C17B3F] hover:bg-[#A8652F] text-white shadow-md hover:shadow-lg"
              : "bg-[#E8E4DC] text-[#B0A89E] cursor-not-allowed"
          }`}
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
                <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" className="opacity-75" />
              </svg>
              Submitting…
            </span>
          ) : (
            <>Submit transfer request <ChevronRight className="w-4 h-4" /></>
          )}
        </button>
      </div>
    </div>
  );
}